<template>
  <div class="stat-nested-pie-chart">
    <div class="chart-head">
      <div class="chart-title">{{ title }}</div>
      <slot name="controls" />
    </div>
    <VChart
      v-if="rows.length"
      class="chart"
      :option="option"
      :style="{ height: height + 'px' }"
      autoresize
      @click="onClick"
      @legendselectchanged="$emit('legendselectchanged', $event)"
    />
    <div v-else class="chart-empty" :style="{ height: height + 'px' }">{{ emptyText }}</div>
  </div>
</template>

<script>
import { computed } from 'vue';
import VChart from '@/components/charts/echarts';
import { DOWNTIME_STATUSES } from '../downtimeStatuses.mjs';

const PALETTE = [
  '#007bff',
  '#12b76a',
  '#f79009',
  '#f04438',
  '#9e77ed',
  '#0ba5ec',
  '#e04f1f',
  '#66c2a5',
  '#f6c445',
  '#8d98ae',
  '#d6708e',
  '#475467',
];

export default {
  name: 'StatNestedPieChart',
  components: { VChart },
  props: {
    title: { type: String, default: '' },
    // rows: [{ label, values: { confirmed: n, ... } }] — те же данные, что у stacked-бара
    rows: { type: Array, default: () => [] },
    height: { type: Number, default: 360 },
    emptyText: { type: String, default: 'Нет данных' },
    valueFormatter: { type: Function, default: (v) => v },
    // Явная полная карта видимости статусов легенды ({ label: bool }); null = все вкл.
    legendSelected: { type: Object, default: null },
  },
  emits: ['select-object', 'legendselectchanged'],
  setup(props, { emit }) {
    const option = computed(() => ({
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: (p) => {
          if (!p) return '';
          const value = props.valueFormatter(p.value);
          // Внешнее кольцо помечено полем object (статус внутри объекта),
          // внутреннее — флагом isObject (объект, итого по статусам).
          if (p.data && p.data.object) {
            return `${p.marker} <b>${p.data.object}</b><br/>${p.name}: <b>${value}</b>`;
          }
          return `${p.marker} <b>${p.name}</b><br/>Итого: <b>${value}</b>`;
        },
      },
      legend: {
        orient: 'vertical',
        right: 0,
        top: 'middle',
        type: 'scroll',
        textStyle: { fontSize: 11, color: '#667085', width: 170, overflow: 'truncate' },
        tooltip: { show: true },
        data: DOWNTIME_STATUSES.map((status) => ({
          name: status.label,
          itemStyle: { color: status.chartColor },
        })),
        ...(props.legendSelected ? { selected: props.legendSelected } : {}),
      },
      series: [
        {
          type: 'pie',
          radius: ['26%', '48%'],
          center: ['30%', '50%'],
          label: { show: false },
          emphasis: { label: { show: false } },
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          data: props.rows.map((row, index) => ({
            name: row.label,
            value: Object.values(row.values || {}).reduce((sum, v) => sum + (Number(v) || 0), 0),
            isObject: true,
            itemStyle: { color: PALETTE[index % PALETTE.length] },
          })),
        },
        {
          type: 'pie',
          radius: ['52%', '76%'],
          center: ['30%', '50%'],
          label: { show: false },
          emphasis: { label: { show: false } },
          itemStyle: { borderColor: '#fff', borderWidth: 2, borderRadius: 3 },
          data: props.rows.flatMap((row) =>
            DOWNTIME_STATUSES.map((status) => ({
              name: status.label,
              value: Number(row.values?.[status.value]) || 0,
              object: row.label,
              itemStyle: { color: status.chartColor },
            })).filter((item) => item.value > 0)
          ),
        },
      ],
    }));

    // Клик по объекту (внутреннему кольцу) — выбор фильтра.
    const onClick = (params) => {
      if (params && params.data && params.data.isObject) emit('select-object', params.name);
    };

    return { option, onClick };
  },
};
</script>

<style scoped>
.stat-nested-pie-chart {
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
}

.chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.chart {
  width: 100%;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #98a2b3;
  font-size: 14px;
}
</style>
